import { useReadContract } from "wagmi";
import { CONFIG } from "../config";
import type { StakingContractData } from "../types";
import { publicClient } from "../lib/viem";
import { useEffect, useState } from "react";

const stakingContractAbi = [
  {
    type: "function",
    name: "getPools",
    stateMutability: "view",
    inputs: [],
    outputs: [
      { type: "uint256[]", name: "poolIds" },
      { type: "address[]", name: "stakingTokens" },
      { type: "uint256[]", name: "durations" },
      { type: "uint256[]", name: "rewards" },
    ],
  },
  {
    type: "function",
    name: "balance",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "participants",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  }
] as const;

export function useStakingContract() {
  const { data: wagmiBalance } = useReadContract({
    address: CONFIG.STAKING_CONTRACT as `0x${string}`,
    abi: stakingContractAbi,
    functionName: "balance",
  });

  const { data: wagmiParticipants } = useReadContract({
    address: CONFIG.STAKING_CONTRACT as `0x${string}`,
    abi: stakingContractAbi,
    functionName: "participants",
  });

  // Fallback to direct RPC calls if wagmi hooks return undefined
  const [balance, setBalance] = useState<bigint | undefined>(wagmiBalance);
  const [participants, setParticipants] = useState<bigint | undefined>(wagmiParticipants);

  useEffect(() => {
    async function fetchData() {
      try {
        if (!wagmiBalance) {
          const result = await publicClient.readContract({
            address: CONFIG.STAKING_CONTRACT as `0x${string}`,
            abi: stakingContractAbi,
            functionName: "balance",
          });
          setBalance(result as bigint);
        }

        if (!wagmiParticipants) {
          const result = await publicClient.readContract({
            address: CONFIG.STAKING_CONTRACT as `0x${string}`,
            abi: stakingContractAbi,
            functionName: "participants",
          });
          setParticipants(result as bigint);
        }
      } catch (error) {
        console.error("Error fetching staking contract data:", error);
      }
    }

    if (!wagmiBalance || !wagmiParticipants) {
      fetchData();
    }
  }, [wagmiBalance, wagmiParticipants]);

  const isLoading = balance === undefined || participants === undefined;

  if (isLoading) {
    return { data: undefined, isLoading: true, error: null };
  }

  const data: StakingContractData = {
    initialized: true,
    owner: "",
    participants: participants || 0n,
    paused: false,
    totalStakedTokens: 0n,
    totalStakedNFTs: 0n,
    rewardPoolSize: balance || 0n,
    rewardRate: 0n,
    stakingStartTime: 0n,
    stakingEndTime: 0n,
    minStakingAmount: 0n,
    maxStakingAmount: 0n,
    earlyWithdrawalPenalty: 0n
  };

  return {
    data,
    isLoading: false,
    error: null
  };
}